// Copyright ©️ 2026 eVolve MEP, LLC

import { Badge, Button, Title, Transition, Paper, Group, Divider, Box, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { EvolveIcon } from 'assets/icons/EvolveIcon';

import { useBendCreatorCartContext } from '../Context/useBendCreatorCartContext';
import type { BendCreatorStep } from './BendCreatorStepPanel';
import { BendListBend } from './BendListBend';
import { NavArrowActionIcon } from './NavArrowActionIcon';

type Props = {
  step: BendCreatorStep | null;
  onChangeStep: (step: BendCreatorStep) => void;
};

const WrappedCreatorBendList = (props: Props) => {
  const { cart, onSubmitCart } = useBendCreatorCartContext();
  const [isOpen, { open, close }] = useDisclosure(true);

  return (
    <Box pos="relative">
      <Transition mounted={!isOpen} transition="fade-left">
        {(style) => (
          <Button
            pos="absolute"
            top={0}
            right={0}
            variant="light"
            size="lg"
            onClick={open}
            rightSection={<Badge>{cart.length}</Badge>}
            style={style}
            w={175}
          >
            Bend List
          </Button>
        )}
      </Transition>
      <Transition mounted={isOpen} transition="fade-left">
        {(style) => (
          <Paper pos="absolute" top={0} right={0} withBorder shadow="sm" w="100%" style={style}>
            <Group justify="space-between" p="md">
              <Title order={5}>Bend List</Title>
              <NavArrowActionIcon onClick={close} direction="right" />
            </Group>
            <Divider />
            {cart.map((bend, i) => (
              <BendListBend key={bend.draftId} index={i + 1} bend={bend} {...props} />
            ))}
            <Divider />
            <Stack p="sm">
              <Button leftSection={<EvolveIcon icon="Check" />} size="xl" fz="sm" onClick={onSubmitCart}>
                Add to work order
              </Button>
            </Stack>
          </Paper>
        )}
      </Transition>
    </Box>
  );
};

/** Wraps over the bend creator cart, so that the collapse state is lost when the cart becomes empty */
export const CreatorBendList = (props: Props) => {
  const { cart } = useBendCreatorCartContext();
  return cart.length > 0 ? <WrappedCreatorBendList {...props} /> : null;
};
